import React, { useState, useEffect } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  CalendarIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { Event, EventType } from '../../types/event';
import { eventsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isStaff } from '../../types/auth';
import { navigateMonth, navigateWeek } from '../../utils/calendar';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import UpcomingEventsList from './UpcomingEventsList';
import EventForm from '../events/EventForm';
import EventDetailModal from '../events/EventDetailModal';
import MatchDetailView from '../match/MatchDetailView';
import DeleteConfirmModal from '../ui/DeleteConfirmModal';
import { usePageTitle } from '../../hooks/usePageTitle';

type CalendarViewType = 'month' | 'week' | 'day';
type CalendarMode = 'calendar' | 'list';

const Calendar: React.FC = () => {
  usePageTitle('Naptár');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<CalendarViewType>('month');
  const [mode, setMode] = useState<CalendarMode>('calendar');
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isMatchDetailOpen, setIsMatchDetailOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    // A naptár nézetben használjuk az összes eseményt;
    // a listanézet saját lekérést használ (közelgő eseményekre).
    if (mode === 'calendar') {
      loadEvents();
    }
  }, [currentDate, view, mode]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await eventsAPI.getAll();
      setEvents(data);
    } catch (err) {
      setError('Hiba történt az események betöltésekor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (view === 'month') {
      setCurrentDate(navigateMonth(currentDate, direction));
    } else if (view === 'week') {
      setCurrentDate(navigateWeek(currentDate, direction));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
      setCurrentDate(newDate);
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleCreateEvent = (date?: Date) => {
    setSelectedEvent(null);
    setSelectedDate(date || currentDate);
    setIsEventModalOpen(true);
  };

  const handleEventClick = (event: Event) => {
    // Mindenki a részletnézetet kapja először - onnan tud szerkeszteni és
    // törölni is (lásd EventDetailModal canModify). Stábtagnál korábban ez
    // közvetlenül a szerkesztő űrlapra ugrott, aminek nincs törlés gombja -
    // a naptárból emiatt nem lehetett eseményt törölni.
    setSelectedEvent(event);
    setSelectedDate(null);
    setIsDetailModalOpen(true);
  };

  const handleEditFromDetail = () => {
    setIsDetailModalOpen(false);
    setIsEventModalOpen(true);
  };

  const handleDeleteEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsDeleteModalOpen(true);
  };

  const handleEventSaved = () => {
    loadEvents();
    setIsEventModalOpen(false);
    setSelectedEvent(null);
    setSelectedDate(null);
  };

  const handleEventDeleted = async () => {
    if (selectedEvent) {
      try {
        await eventsAPI.delete(selectedEvent.id);
        loadEvents();
        setIsDeleteModalOpen(false);
        setSelectedEvent(null);
      } catch (err) {
        setError('Hiba történt az esemény törlésekor');
        console.error(err);
      }
    }
  };

  const canModifyEvents = isStaff(user);

  const getViewTitle = () => {
    switch (view) {
      case 'month':
        return format(currentDate, 'yyyy MMMM', { locale: hu });
      case 'week':
        return `${format(currentDate, 'yyyy MMMM', { locale: hu })} - ${format(currentDate, 'wo', { locale: hu })} hét`;
      case 'day':
        return format(currentDate, 'yyyy MMMM d, EEEE', { locale: hu });
      default:
        return '';
    }
  };

  if (loading && mode === 'calendar') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b bg-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        {/* Bal oldal: cím (+ desktopon navigáció és dátum, mobilon mód váltó) */}
        <div className="flex items-center justify-between gap-3 sm:justify-start sm:gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Naptár</h1>

          {/* Desktop: Ma + nyilak + dátum */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={handleToday}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Ma
            </button>
            <button
              onClick={() => handleNavigate('prev')}
              aria-label="Előző"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleNavigate('next')}
              aria-label="Következő"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
          <h2 className="hidden sm:block text-lg font-medium text-gray-900 capitalize">
            {getViewTitle()}
          </h2>

          {/* Mobil: Naptár/Lista mód váltó a cím mellett */}
          {canModifyEvents && (
            <div className="flex sm:hidden rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setMode('calendar')}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                  mode === 'calendar'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CalendarIcon className="h-4 w-4" />
                <span>Naptár</span>
              </button>
              <button
                onClick={() => setMode('list')}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                  mode === 'list'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ListBulletIcon className="h-4 w-4" />
                <span>Lista</span>
              </button>
            </div>
          )}
        </div>

        {/* Mobil: dátum-navigációs sor (Ma, nyilak, középen a dátum) */}
        {mode === 'calendar' && (
          <div className="flex items-center gap-1 sm:hidden">
            <button
              onClick={handleToday}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Ma
            </button>
            <button
              onClick={() => handleNavigate('prev')}
              aria-label="Előző"
              className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <h2 className="flex-1 truncate text-center text-base font-medium text-gray-900 capitalize">
              {getViewTitle()}
            </h2>
            <button
              onClick={() => handleNavigate('next')}
              aria-label="Következő"
              className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Mobil: nézetváltó teljes szélességben */}
        {mode === 'calendar' && (
          <div className="grid grid-cols-3 rounded-lg border border-gray-300 overflow-hidden sm:hidden">
            {([
              { key: 'month', label: 'Hónap', icon: Squares2X2Icon },
              { key: 'week', label: 'Hét', icon: ViewColumnsIcon },
              { key: 'day', label: 'Nap', icon: CalendarIcon },
            ] as const).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                className={`px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${
                  view === key
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Jobb oldal (csak desktop): nézetváltó + mód váltó + új esemény */}
        <div className="hidden sm:flex flex-wrap items-center gap-2 sm:gap-3">
          {/* View Switcher (csak naptár módban értelmezett) */}
          {mode === 'calendar' && (
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setView('month')}
                aria-label="Havi nézet"
                title="Havi nézet"
                aria-pressed={view === 'month'}
                className={`px-3 py-2 text-sm font-medium rounded-l-lg inline-flex items-center justify-center ${
                  view === 'month'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setView('week')}
                aria-label="Heti nézet"
                title="Heti nézet"
                aria-pressed={view === 'week'}
                className={`px-3 py-2 text-sm font-medium inline-flex items-center justify-center ${
                  view === 'week'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ViewColumnsIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setView('day')}
                aria-label="Napi nézet"
                title="Napi nézet"
                aria-pressed={view === 'day'}
                className={`px-3 py-2 text-sm font-medium rounded-r-lg inline-flex items-center justify-center ${
                  view === 'day'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Mód váltó: Naptár / Lista (csak staffnak) */}
          {canModifyEvents && (
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setMode('calendar')}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                  mode === 'calendar'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CalendarIcon className="h-4 w-4" />
                <span>Naptár</span>
              </button>
              <button
                onClick={() => setMode('list')}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                  mode === 'list'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ListBulletIcon className="h-4 w-4" />
                <span>Lista</span>
              </button>
            </div>
          )}

          {/* Új esemény - mobilon is ez az egyetlen belépési pont */}
          {canModifyEvents && mode === 'calendar' && (
            <button
              onClick={() => handleCreateEvent()}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Új esemény
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Tartalom: naptár vagy lista */}
      <div className="flex-1 overflow-hidden">
        {mode === 'calendar' ? (
          <>
            {view === 'month' && (
              <MonthView
                currentDate={currentDate}
                events={events}
                onEventClick={handleEventClick}
                onDateClick={handleCreateEvent}
                canModifyEvents={canModifyEvents}
              />
            )}
            {view === 'week' && (
              <WeekView
                currentDate={currentDate}
                events={events}
                onEventClick={handleEventClick}
                onTimeSlotClick={handleCreateEvent}
                canModifyEvents={canModifyEvents}
              />
            )}
            {view === 'day' && (
              <DayView
                currentDate={currentDate}
                events={events}
                onEventClick={handleEventClick}
                onTimeSlotClick={handleCreateEvent}
                canModifyEvents={canModifyEvents}
              />
            )}
          </>
        ) : (
          <UpcomingEventsList />
        )}
      </div>

      {/* Modals */}
      <EventForm
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
          setSelectedDate(null);
        }}
        event={selectedEvent}
        selectedDate={selectedDate}
        onSave={handleEventSaved}
      />

      <EventDetailModal
        event={selectedEvent}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedEvent(null);
        }}
        onEdit={handleEditFromDetail}
        onDelete={() => {
          setIsDetailModalOpen(false);
          if (selectedEvent) handleDeleteEvent(selectedEvent);
        }}
        onResponseChange={loadEvents}
        onOpenMatch={selectedEvent?.type === EventType.Match ? () => {
          setIsDetailModalOpen(false);
          setIsMatchDetailOpen(true);
        } : undefined}
      />

      <MatchDetailView
        event={selectedEvent}
        isOpen={isMatchDetailOpen}
        onClose={() => {
          setIsMatchDetailOpen(false);
        }}
        onEditEvent={selectedEvent ? handleEditFromDetail : undefined}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedEvent(null);
        }}
        onConfirm={handleEventDeleted}
        title="Esemény törlése"
        message={`Biztosan törölni szeretnéd a következő eseményt: ${selectedEvent?.title}?`}
        itemName={selectedEvent?.title || ''}
      />
    </div>
  );
};

export default Calendar;